import React from 'react';
import styled, { keyframes } from 'styled-components';

const wipe = keyframes`
  0% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: -317.3;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
  }
  25% {
    transform: rotate(90deg) scale(1.03);
  }
  50% {
    transform: rotate(180deg) scale(1);
  }
  75% {
    transform: rotate(270deg) scale(1.03);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
`;

const IllustrationWrapper = styled.div<{ $size?: number }>`
  width: ${props => props.$size || 104}px;
  height: ${props => props.$size || 104}px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .mask-circle {
    transform: rotate(-90deg);
    transform-origin: 51.5px 51.5px;
    stroke-dasharray: 317.3;
    animation: ${wipe} 10s linear infinite;
  }

  .hourglass-graphic {
    transform-origin: 51.5px 51.5px;
    animation: ${spin} 10s linear infinite;
  }
`;

type Props = {
  size?: number;
};

export const AnimatedTimer = ({ size = 104 }: Props) => {
  return (
    <IllustrationWrapper $size={size}>
      <svg width={size} height={size} viewBox="0 0 104 104" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`paint0_linear_2402_26056_${size}`} x1="53.6727" y1="27.0587" x2="53.6727" y2="72.2096" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FFA87F"/>
            <stop offset="0.5" stopColor="#FFA87F"/>
            <stop offset="1" stopColor="#FF8EAB"/>
          </linearGradient>

          <mask id={`countdown-mask_${size}`}>
            <circle 
              cx="51.5098" 
              cy="51.51" 
              r="50.5" 
              stroke="white" 
              strokeWidth="4" 
              strokeLinecap="round"
              className="mask-circle"
            />
          </mask>

          <mask id={`mask0_2402_26056_${size}`} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="104" height="104">
            <circle cx="51.5098" cy="51.51" r="50.5" stroke="#EC9832" strokeWidth="2.02" strokeLinecap="round" strokeDasharray="4.04 4.04"/>
          </mask>
        </defs>

        {/* Underlying grey dashed circle */}
        <circle 
          cx="51.5098" 
          cy="51.51" 
          r="50.5" 
          fill="white" 
          stroke="#E5E7EB" 
          strokeWidth="2.02" 
          strokeLinecap="round" 
          strokeDasharray="4.04 4.04"
        />

        {/* Surrounding orange dashed ring, masked by the countdown-mask */}
        <circle 
          cx="51.5098" 
          cy="51.51" 
          r="50.5" 
          fill="none" 
          stroke="#FFA87F" 
          strokeWidth="2.02" 
          strokeLinecap="round" 
          strokeDasharray="4.04 4.04"
          mask={`url(#countdown-mask_${size})`}
        />

        {/* Hourglass graphics group (hourglass + the grey background element) */}
        <g className="hourglass-graphic">
          {/* Grey background line element from original SVG */}
          <g mask={`url(#mask0_2402_26056_${size})`}>
            <path d="M59.8324 -3.11306C59.9399 -3.90144 59.3881 -4.62966 58.5972 -4.7167C57.8063 -4.80374 57.1093 -4.21294 57.0428 -3.42004L52.5207 50.5L59.8324 -3.11306Z" fill="#DBDBD7"/>
          </g>

          {/* Main Hourglass Path */}
          <path d="M38.2689 34.4361C39.4849 31.8101 43.6603 31.8101 52.0089 31.8101C60.3576 31.8101 64.5329 31.8101 65.749 34.4361C65.854 34.6596 65.9415 34.8926 66.0116 35.135C66.8398 37.9367 63.8865 41.1849 57.9841 47.6792L54.0289 52.0101L57.9841 56.3389C63.8865 62.8352 66.8378 66.0834 66.0116 68.8831C65.9416 69.1236 65.8538 69.3586 65.749 69.5861C64.5329 72.2101 60.3576 72.2101 52.0089 72.2101C43.6603 72.2101 39.4849 72.2101 38.2689 69.5841C38.1642 69.3579 38.0765 69.1243 38.0063 68.8851C37.1781 66.0834 40.1313 62.8352 46.0338 56.3409L49.9889 52.0101L46.0338 47.6812C40.1313 41.1829 37.1821 37.9367 38.0063 35.137C38.0763 34.8946 38.1639 34.6623 38.2689 34.4361Z" fill={`url(#paint0_linear_2402_26056_${size})`}/>
        </g>
      </svg>
    </IllustrationWrapper>
  );
};

export default AnimatedTimer;
